import React, { useEffect, useState } from 'react';
import { Button, Input, Progress, Space, Tag, Tooltip, notification } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { CustomProduct, CustomProductGeneration, refetchCustomProduct, updateCustomProduct } from '../../store/customProductSlice';
import { LengthTags } from './Editors/LengthTags';
import { CompanyAmazonContentSettings } from '../../types/admin';

interface Props {
  product: CustomProduct;
}

type KeywordCoverage = {
  total: number;
  oldCount: number;
  newCount: number;
  oldKeywords: string[];
  addedKeywords: string[];
};

type EditableGeneratedField = 'title' | 'bulletPoints' | 'description';

type ImpactField = {
  label: string;
  oldText: string;
  newText: string;
  keywords: string[];
  priorityKeywords: string[];
  baseWeight: number;
  priorityWeight: number;
};

type ImpactFieldScore = {
  label: string;
  oldCount: number;
  newCount: number;
  total: number;
  oldPriorityCount: number;
  newPriorityCount: number;
  priorityTotal: number;
  addedCount: number;
  oldScore: number;
  newScore: number;
  maxScore: number;
};

type SeoReadinessStatus = 'Well optimized' | 'Saturation risk' | 'Improving' | 'Under-optimized';

type SeoImpact = {
  score: number;
  status: SeoReadinessStatus;
  oldCoverage: number;
  newCoverage: number;
  totalCoverage: number;
  oldPriorityCoverage: number;
  newPriorityCoverage: number;
  totalPriorityCoverage: number;
  oldPlacementScore: number;
  newPlacementScore: number;
  addedPlacements: number;
  improvedFields: ImpactFieldScore[];
  detectedNegativeKeywords: string[];
  repeatedKeywords: string[];
  lengthWarnings: string[];
  saturationWarnings: string[];
  keywordDensity: number;
};

const DEFAULT_AMAZON_CONTENT_SETTINGS = {
  titleMaxChars: 200,
  bulletPointCount: 5,
  descriptionMaxChars: 2000,
};

const normalizeKeyword = (keyword: string) => keyword.trim().replace(/\s+/g, ' ').toLowerCase();

const wordBoundaryChars = '\\p{L}\\p{N}';

const uniqueKeywords = (keywords: Array<string | undefined>) => {
  const seen = new Set<string>();
  return keywords
    .map((keyword) => (keyword || '').trim())
    .filter(Boolean)
    .filter((keyword) => {
      const normalized = normalizeKeyword(keyword);
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanKeywordPhrase = (phrase?: string) => (
  (phrase || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/([(/])\s+/g, '$1')
    .replace(/\s+([)/])/g, '$1')
    .replace(/^[\s,.;:\-_/]+|[\s,.;:\-_/]+$/g, '')
    .replace(/\s+/g, ' ')
);

const getAnchorVariants = (anchorKeywords: string[]) => {
  const variants: string[] = [];
  const seen = new Set<string>();

  uniqueKeywords(anchorKeywords).forEach((anchor) => {
    const cleanedAnchor = cleanKeywordPhrase(anchor);
    const words = cleanedAnchor.split(' ').filter(Boolean);
    const lastWord = words[words.length - 1] || '';
    const pluralVariants = [
      lastWord.endsWith('r') ? [...words.slice(0, -1), `${lastWord}en`].join(' ') : '',
      lastWord.endsWith('e') ? [...words.slice(0, -1), `${lastWord}n`].join(' ') : '',
    ].filter(Boolean);

    [cleanedAnchor, ...pluralVariants].forEach((variant) => {
      const normalized = normalizeKeyword(variant);
      if (!variant || seen.has(normalized)) return;
      variants.push(variant);
      seen.add(normalized);
    });
  });

  return variants.sort((a, b) => b.length - a.length);
};

const isMeaningfulKeywordConcept = (keyword: string) => {
  if (!keyword) return false;
  if (/[0-9°/+\\-]/.test(keyword)) return true;

  const words = Array.from(keyword.matchAll(new RegExp(`[${wordBoundaryChars}]+`, 'gu')));
  if (words.length >= 2) return true;

  return Boolean(words[0]?.[0] && words[0][0].length >= 8);
};

const deriveGenerationKeywords = (keywords: string[], anchorKeywords: string[]) => {
  const anchors = getAnchorVariants(anchorKeywords);
  const anchorSet = new Set(anchors.map(normalizeKeyword));
  const concepts: string[] = [];
  const seen = new Set<string>();

  uniqueKeywords(keywords).forEach((keyword) => {
    const cleanedKeyword = cleanKeywordPhrase(keyword);
    if (!cleanedKeyword || anchorSet.has(normalizeKeyword(cleanedKeyword))) return;

    const concept = cleanKeywordPhrase(anchors.reduce((current, anchor) => (
      current.replace(new RegExp(`(^|[^${wordBoundaryChars}])${escapeRegex(anchor)}(?=$|[^${wordBoundaryChars}])`, 'giu'), '$1 ')
    ), cleanedKeyword));

    const normalizedConcept = normalizeKeyword(concept);
    if (!isMeaningfulKeywordConcept(concept) || seen.has(normalizedConcept)) return;

    concepts.push(concept);
    seen.add(normalizedConcept);
  });

  return concepts;
};

const deriveGenerationAnchorKeywords = (anchorKeywords: string[], brand?: string) => {
  const anchors = uniqueKeywords(anchorKeywords);
  const anchorVariantSet = new Set(getAnchorVariants(anchors).map(normalizeKeyword));
  const normalizedBrand = normalizeKeyword(brand || '');
  const generationAnchors: string[] = [];
  const seen = new Set<string>();

  anchors.forEach((anchor) => {
    let generationAnchor = anchor;

    if (normalizedBrand) {
      const brandPattern = new RegExp(`^\\s*${escapeRegex(brand || '')}(?=$|[^${wordBoundaryChars}])`, 'iu');
      if (brandPattern.test(anchor)) {
        const remainder = cleanKeywordPhrase(anchor.replace(brandPattern, ' '));
        if (remainder && isMeaningfulKeywordConcept(remainder)) {
          if (anchorVariantSet.has(normalizeKeyword(remainder))) return;
          generationAnchor = remainder;
        }
      }
    }

    const normalized = normalizeKeyword(generationAnchor);
    if (seen.has(normalized)) return;
    generationAnchors.push(generationAnchor);
    seen.add(normalized);
  });

  return generationAnchors;
};

const getCoverageKeywords = (
  globalKeywords: string[],
  highPriorityKeywords: string[],
  optionalKeywords: string[],
) => uniqueKeywords([
  ...globalKeywords,
  ...deriveGenerationKeywords([...highPriorityKeywords, ...optionalKeywords], globalKeywords),
]);

const getPriorityCoverageKeywords = (
  globalKeywords: string[],
  highPriorityKeywords: string[],
) => uniqueKeywords([
  ...globalKeywords,
  ...deriveGenerationKeywords(highPriorityKeywords, globalKeywords),
]);

const getKeywordMatchPhrases = (keyword: string) => getAnchorVariants([keyword]);

const textContainsKeyword = (text: string, keyword: string) => {
  if (!text || !keyword) return false;
  return getKeywordMatchPhrases(keyword).some((phrase) => (
    new RegExp(`(^|[^${wordBoundaryChars}])${escapeRegex(phrase)}(?=$|[^${wordBoundaryChars}])`, 'iu').test(text)
  ));
};

const countKeywordOccurrences = (text: string, keyword: string) => {
  if (!text || !keyword) return 0;
  return getKeywordMatchPhrases(keyword).reduce((sum, phrase) => (
    sum + Array.from(text.matchAll(new RegExp(`(^|[^${wordBoundaryChars}])${escapeRegex(phrase)}(?=$|[^${wordBoundaryChars}])`, 'giu'))).length
  ), 0);
};

const countNonOverlappingKeywordPlacements = (text: string, keywords: string[]) => {
  const sortedKeywords = uniqueKeywords(keywords).sort((a, b) => b.length - a.length);
  if (!text || sortedKeywords.length === 0) return 0;

  const spans = sortedKeywords.flatMap((keyword) => (
    getKeywordMatchPhrases(keyword).flatMap((phrase) => Array.from(text.matchAll(new RegExp(`(^|[^${wordBoundaryChars}])${escapeRegex(phrase)}(?=$|[^${wordBoundaryChars}])`, 'giu')))
      .map((match) => {
        const prefix = match[1] || '';
        const matchedText = match[2] || '';
        const start = (match.index ?? 0) + prefix.length;
        return { start, end: start + matchedText.length };
      }))
  )).sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));

  return spans.reduce((accepted, span) => {
    const overlaps = accepted.some((acceptedSpan) => (
      span.start < acceptedSpan.end && span.end > acceptedSpan.start
    ));
    return overlaps ? accepted : [...accepted, span];
  }, [] as Array<{ start: number; end: number }>).length;
};

const countWords = (text: string) => (
  Array.from(text.matchAll(/[\p{L}\p{N}]+/gu)).length
);

const getTextFromHtml = (html?: string) => {
  if (!html) return '';
  if (typeof window !== 'undefined' && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }
  return html.replace(/<[^>]*>/g, ' ');
};

const getValuesFromRecord = (record?: Record<string, string>) => (
  record ? Object.values(record).filter(Boolean) : []
);

const getKeywordCoverage = (oldText: string, newText: string, keywords: string[]): KeywordCoverage => {
  const oldMatches = keywords.filter((keyword) => textContainsKeyword(oldText, keyword));
  const newMatches = keywords.filter((keyword) => textContainsKeyword(newText, keyword));

  return {
    total: keywords.length,
    oldCount: oldMatches.length,
    newCount: newMatches.length,
    oldKeywords: oldMatches,
    addedKeywords: newMatches.filter((keyword) => !oldMatches.some((oldKeyword) => normalizeKeyword(oldKeyword) === normalizeKeyword(keyword))),
  };
};

const scoreImpactField = (field: ImpactField): ImpactFieldScore => {
  const keywords = uniqueKeywords(field.keywords);
  const priorityKeywords = uniqueKeywords(field.priorityKeywords);
  const prioritySet = new Set(priorityKeywords.map(normalizeKeyword));
  const oldMatches = keywords.filter((keyword) => textContainsKeyword(field.oldText, keyword));
  const newMatches = keywords.filter((keyword) => textContainsKeyword(field.newText, keyword));
  const oldPriorityMatches = priorityKeywords.filter((keyword) => textContainsKeyword(field.oldText, keyword));
  const newPriorityMatches = priorityKeywords.filter((keyword) => textContainsKeyword(field.newText, keyword));

  const getWeight = (keyword: string) => (
    prioritySet.has(normalizeKeyword(keyword)) ? field.priorityWeight : field.baseWeight
  );

  return {
    label: field.label,
    oldCount: oldMatches.length,
    newCount: newMatches.length,
    total: keywords.length,
    oldPriorityCount: oldPriorityMatches.length,
    newPriorityCount: newPriorityMatches.length,
    priorityTotal: priorityKeywords.length,
    addedCount: newMatches.filter((keyword) => !oldMatches.some((oldKeyword) => normalizeKeyword(oldKeyword) === normalizeKeyword(keyword))).length,
    oldScore: oldMatches.reduce((sum, keyword) => sum + getWeight(keyword), 0),
    newScore: newMatches.reduce((sum, keyword) => sum + getWeight(keyword), 0),
    maxScore: keywords.reduce((sum, keyword) => sum + getWeight(keyword), 0),
  };
};

const clampScore = (score: number) => Math.max(0, Math.min(100, Math.round(score)));

const getReadinessStatus = (score: number, hasSaturationRisk: boolean): SeoReadinessStatus => {
  if (hasSaturationRisk) return 'Saturation risk';
  if (score >= 45) return 'Well optimized';
  if (score >= 20) return 'Improving';
  return 'Under-optimized';
};

const calculateSeoImpact = ({
  fields,
  negativeKeywords,
  generatedTitle,
  generatedBulletPoints,
  generatedDescription,
  amazonContentSettings,
}: {
  fields: ImpactField[];
  negativeKeywords: string[];
  generatedTitle: string;
  generatedBulletPoints: string[];
  generatedDescription: string;
  amazonContentSettings?: CompanyAmazonContentSettings;
}): SeoImpact | null => {
  const fieldScores = fields.map(scoreImpactField);
  const totalCoverage = fieldScores.reduce((sum, field) => sum + field.total, 0);
  if (totalCoverage === 0) return null;

  const oldCoverage = fieldScores.reduce((sum, field) => sum + field.oldCount, 0);
  const newCoverage = fieldScores.reduce((sum, field) => sum + field.newCount, 0);
  const totalPriorityCoverage = fieldScores.reduce((sum, field) => sum + field.priorityTotal, 0);
  const oldPriorityCoverage = fieldScores.reduce((sum, field) => sum + field.oldPriorityCount, 0);
  const newPriorityCoverage = fieldScores.reduce((sum, field) => sum + field.newPriorityCount, 0);
  const oldPlacementScore = fieldScores.reduce((sum, field) => sum + field.oldScore, 0);
  const newPlacementScore = fieldScores.reduce((sum, field) => sum + field.newScore, 0);
  const maxPlacementScore = fieldScores.reduce((sum, field) => sum + field.maxScore, 0);
  const addedPlacements = fieldScores.reduce((sum, field) => sum + field.addedCount, 0);
  const generatedText = [generatedTitle, ...generatedBulletPoints, generatedDescription].join('\n');
  const detectedNegativeKeywords = uniqueKeywords(negativeKeywords).filter((keyword) => textContainsKeyword(generatedText, keyword));
  const allKeywords = uniqueKeywords(fields.flatMap((field) => field.keywords));
  const keywordOccurrences = countNonOverlappingKeywordPlacements(generatedText, allKeywords);
  const wordCount = countWords(generatedText);
  const keywordDensity = wordCount ? Number(((keywordOccurrences / wordCount) * 100).toFixed(1)) : 0;
  const repeatedKeywords = allKeywords
    .filter((keyword) => countKeywordOccurrences(generatedText, keyword) >= 7)
    .slice(0, 3);
  const highCoverageFields = fieldScores
    .filter((field) => field.total >= 8 && field.newCount / field.total >= 0.95)
    .map((field) => field.label);
  const hasSaturationRisk = (
    keywordDensity >= 22
    || repeatedKeywords.length >= 2
    || (highCoverageFields.length >= 2 && keywordDensity >= 16)
  );
  const saturationWarnings = [
    keywordDensity >= 22 ? `Keyword density is high at ${keywordDensity}%` : '',
    keywordDensity >= 16 && keywordDensity < 22 ? `Keyword use is near the healthy upper range at ${keywordDensity}%` : '',
    highCoverageFields.length > 0 ? `${highCoverageFields.join(', ')} coverage is near saturation` : '',
    repeatedKeywords.length > 0 ? `Repeated terms may make the copy feel stuffed: ${repeatedKeywords.join(', ')}` : '',
  ].filter(Boolean);
  const titleMaxChars = amazonContentSettings?.titleMaxChars ?? DEFAULT_AMAZON_CONTENT_SETTINGS.titleMaxChars;
  const descriptionMaxChars = amazonContentSettings?.descriptionMaxChars ?? DEFAULT_AMAZON_CONTENT_SETTINGS.descriptionMaxChars;
  const lengthWarnings = [
    generatedTitle.length > titleMaxChars ? `Title is over ${titleMaxChars} characters` : '',
    ...generatedBulletPoints.map((bulletPoint, index) => (
      bulletPoint.length > 250 ? `Bullet ${index + 1} is over 250 characters` : ''
    )),
    generatedDescription.length > descriptionMaxChars ? `Description is over ${descriptionMaxChars} characters` : '',
  ].filter(Boolean);
  const coverageLift = maxPlacementScore ? ((newPlacementScore - oldPlacementScore) / maxPlacementScore) * 100 : 0;
  const finalCoverage = maxPlacementScore ? (newPlacementScore / maxPlacementScore) * 100 : 0;
  const saturationPenalty = (
    (keywordDensity >= 22 ? 12 : keywordDensity >= 16 ? 4 : 0)
    + (highCoverageFields.length * 2)
    + (repeatedKeywords.length * 3)
  );
  const penalty = detectedNegativeKeywords.length * 15 + lengthWarnings.length * 5 + saturationPenalty;
  const score = clampScore((coverageLift * 0.7) + (finalCoverage * 0.3) - penalty);

  return {
    score,
    status: getReadinessStatus(score, hasSaturationRisk),
    oldCoverage,
    newCoverage,
    totalCoverage,
    oldPriorityCoverage,
    newPriorityCoverage,
    totalPriorityCoverage,
    oldPlacementScore,
    newPlacementScore,
    addedPlacements,
    improvedFields: fieldScores.filter((field) => field.addedCount > 0).sort((a, b) => b.addedCount - a.addedCount),
    detectedNegativeKeywords,
    repeatedKeywords,
    lengthWarnings,
    saturationWarnings,
    keywordDensity,
  };
};

type HighlightGroup = {
  keywords: string[];
  className: string;
};

const renderHighlightedText = (text: string, keywords: string[], className = 'custom-product-highlight') => (
  renderHighlightedTextGroups(text, [{ keywords, className }])
);

const renderHighlightedTextGroups = (text: string, groups: HighlightGroup[]) => {
  const highlightKeywords = uniqueKeywords(groups.flatMap((group) => group.keywords).flatMap(getKeywordMatchPhrases))
    .sort((a, b) => b.length - a.length);
  if (!text || highlightKeywords.length === 0) return text || '—';

  const classByKeyword = new Map<string, string>();
  groups.forEach((group) => {
    group.keywords.flatMap(getKeywordMatchPhrases).forEach((keyword) => {
      const normalized = normalizeKeyword(keyword);
      if (classByKeyword.has(normalized)) return;
      classByKeyword.set(normalized, group.className);
    });
  });

  const regex = new RegExp(`(^|[^${wordBoundaryChars}])(${highlightKeywords.map(escapeRegex).join('|')})(?=$|[^${wordBoundaryChars}])`, 'giu');
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const prefix = match[1] || '';
    const matchedText = match[2] || '';
    const keywordStart = match.index + prefix.length;

    if (keywordStart > lastIndex) {
      parts.push(text.slice(lastIndex, keywordStart));
    }

    parts.push(
      <mark key={`${keywordStart}-${matchedText}`} className={classByKeyword.get(normalizeKeyword(matchedText)) || 'custom-product-highlight'}>
        {matchedText}
      </mark>
    );

    lastIndex = keywordStart + matchedText.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
};

const CoverageTags: React.FC<{ coverage: KeywordCoverage }> = ({ coverage }) => (
  <Space size={4} wrap>
    <Tag>Old {coverage.oldCount}/{coverage.total}</Tag>
    <Tag color="success">New {coverage.newCount}/{coverage.total}</Tag>
    {coverage.addedKeywords.length > 0 && (
      <Tag color="warning">+{coverage.addedKeywords.length} added</Tag>
    )}
  </Space>
);

const SeoImpactPanel: React.FC<{ impact: SeoImpact | null }> = ({ impact }) => {
  if (!impact) return null;

  const isSaturationRisk = impact.status === 'Saturation risk';
  const impactColor = isSaturationRisk ? '#ff7a00' : impact.score >= 45 ? '#0d9488' : impact.score >= 20 ? '#ff7a00' : '#6b7280';
  const strongestFields = impact.improvedFields.slice(0, 2).map((field) => `${field.label} +${field.addedCount}`).join(', ');
  const guardrails = [
    impact.status === 'Well optimized'
      ? 'Healthy keyword range reached'
      : impact.status === 'Saturation risk'
        ? 'Adding more keywords may reduce readability'
        : 'More relevant keyword coverage is still possible',
    ...impact.saturationWarnings.slice(0, 2),
    impact.detectedNegativeKeywords.length === 0
      ? 'No negative keywords detected'
      : `Review negative keywords: ${impact.detectedNegativeKeywords.join(', ')}`,
    impact.lengthWarnings.length === 0
      ? 'Marketplace length checks look good'
      : impact.lengthWarnings.slice(0, 2).join(', '),
  ];

  return (
    <div className="custom-product-seo-impact-card">
      <div className="custom-product-seo-impact-summary">
        <div>
          <div className="custom-product-seo-impact-eyebrow">Content SEO Readiness</div>
          <div className="custom-product-seo-impact-title">
            {impact.status}
            <Tag color={isSaturationRisk ? 'warning' : impact.score >= 45 ? 'success' : impact.score >= 20 ? 'warning' : 'default'}>{impact.score}/100 balance</Tag>
          </div>
        </div>
        <Progress
          className="custom-product-seo-impact-progress"
          percent={impact.score}
          showInfo={false}
          strokeColor={impactColor}
        />
      </div>

      <div className="custom-product-seo-impact-metrics">
        <div>
          <span>Keyword placements</span>
          <strong>{impact.oldCoverage}/{impact.totalCoverage}{' -> '}{impact.newCoverage}/{impact.totalCoverage}</strong>
        </div>
        <div>
          <span>Priority coverage</span>
          <strong>{impact.oldPriorityCoverage}/{impact.totalPriorityCoverage}{' -> '}{impact.newPriorityCoverage}/{impact.totalPriorityCoverage}</strong>
        </div>
        <div>
          <span>Weighted relevance</span>
          <strong>{impact.oldPlacementScore}{' -> '}{impact.newPlacementScore}</strong>
        </div>
      </div>

      <div className="custom-product-seo-impact-reasons">
        <span>+{impact.addedPlacements} recommended keyword placements added</span>
        {strongestFields && <span>Strongest gains: {strongestFields}</span>}
        <span>Keyword density: {impact.keywordDensity}%</span>
        {guardrails.map((guardrail) => <span key={guardrail}>{guardrail}</span>)}
      </div>
    </div>
  );
};

const ComparisonBlock: React.FC<{
  label: string;
  oldLabel: string;
  newLabel: string;
  oldText?: string;
  newText: string;
  keywords: string[];
  lengthTags?: React.ReactNode;
  onEdit?: () => void;
}> = ({ label, oldLabel, newLabel, oldText, newText, keywords, lengthTags, onEdit }) => {
  const oldContent = oldText || '';
  const coverage = getKeywordCoverage(oldContent, newText, keywords);

  return (
    <section>
      <div className="custom-product-section-heading">
        <h3 className="custom-product-section-title">{label}</h3>
        <CoverageTags coverage={coverage} />
      </div>
      <div className="custom-product-card">
        <div className="custom-product-comparison-header">
          <div className="custom-product-comparison-header-cell">{oldLabel}</div>
          <div className="custom-product-comparison-header-cell">{newLabel}</div>
        </div>
        <div className="custom-product-comparison-grid">
          <div className="custom-product-comparison-cell">
            <p className="custom-product-comparison-text">
              {renderHighlightedText(oldContent, coverage.oldKeywords, 'custom-product-highlight custom-product-highlight--old')}
            </p>
          </div>
          <div className="custom-product-comparison-cell custom-product-generated-cell" onClick={onEdit}>
            <p className="custom-product-comparison-text">
              {renderHighlightedTextGroups(newText, [
                {
                  keywords: coverage.addedKeywords,
                  className: 'custom-product-highlight',
                },
                {
                  keywords: coverage.oldKeywords,
                  className: 'custom-product-highlight custom-product-highlight--old',
                },
              ])}
            </p>
            {lengthTags && <div className="custom-product-length-tags">{lengthTags}</div>}
          </div>
        </div>
      </div>
    </section>
  );
};

const BulletPointComparisonBlock: React.FC<{
  oldBulletPoints: string[];
  newBulletPoints: string[];
  keywords: string[];
  onEdit?: () => void;
}> = ({ oldBulletPoints, newBulletPoints, keywords, onEdit }) => {
  const oldText = oldBulletPoints.join('\n');
  const newText = newBulletPoints.join('\n');
  const coverage = getKeywordCoverage(oldText, newText, keywords);
  const rowCount = Math.max(oldBulletPoints.length, newBulletPoints.length, 1);

  return (
    <section>
      <div className="custom-product-section-heading">
        <h3 className="custom-product-section-title">Generated Bullet Points</h3>
        <CoverageTags coverage={coverage} />
      </div>

      <div className="custom-product-card">
        <div className="custom-product-bullet-header">
          <div className="custom-product-bullet-header-cell">Old Key Benefits</div>
          <div className="custom-product-bullet-header-cell">Generated Bullet Point</div>
        </div>

        {Array.from({ length: rowCount }).map((_, index) => {
          const oldBulletPoint = oldBulletPoints[index] || '';
          const newBulletPoint = newBulletPoints[index] || '';

          return (
            <div key={index} className="custom-product-bullet-row">
              <div className="custom-product-bullet-cell">
                <div className="custom-product-bullet-copy">
                  <span className="custom-product-bullet-number">{index + 1}</span>
                  <p className="custom-product-comparison-text">
                    {renderHighlightedText(oldBulletPoint, coverage.oldKeywords, 'custom-product-highlight custom-product-highlight--old')}
                  </p>
                </div>
              </div>
              <div className="custom-product-bullet-cell custom-product-generated-cell" onClick={onEdit}>
                <div className="custom-product-bullet-copy">
                  <span className="custom-product-bullet-number">{index + 1}</span>
                  <div>
                    <p className="custom-product-comparison-text">
                      {renderHighlightedTextGroups(newBulletPoint, [
                        {
                          keywords: coverage.addedKeywords,
                          className: 'custom-product-highlight',
                        },
                        {
                          keywords: coverage.oldKeywords,
                          className: 'custom-product-highlight custom-product-highlight--old',
                        },
                      ])}
                    </p>
                    {newBulletPoint && (
                      <div className="custom-product-length-tags">
                        <LengthTags value={newBulletPoint} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const InlineEditActions: React.FC<{
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ saving, onSave, onDiscard }) => (
  <div className="custom-product-edit-actions">
    <Button type="text" onClick={onDiscard} disabled={saving}>
      Discard
    </Button>
    <Button type="primary" onClick={onSave} loading={saving}>
      Save Changes
    </Button>
  </div>
);

const InlineEditBlock: React.FC<{
  label: string;
  oldLabel?: string;
  newLabel?: string;
  oldContent?: React.ReactNode;
  children: React.ReactNode;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ label, oldLabel = 'Old Content', newLabel = 'Generated Content', oldContent, children, saving, onSave, onDiscard }) => (
  <section>
    <div className="custom-product-section-heading">
      <h3 className="custom-product-section-title">{label}</h3>
    </div>
    <div className="custom-product-card custom-product-card--editing">
      <div className="custom-product-comparison-header">
        <div className="custom-product-comparison-header-cell">{oldLabel}</div>
        <div className="custom-product-comparison-header-cell">{newLabel}</div>
      </div>
      <div className="custom-product-comparison-grid">
        <div className="custom-product-comparison-cell">
          <p className="custom-product-comparison-text">
          {oldContent || '—'}
          </p>
        </div>
        <div className="custom-product-comparison-cell">
        {children}
        <InlineEditActions saving={saving} onSave={onSave} onDiscard={onDiscard} />
        </div>
      </div>
    </div>
  </section>
);

const BulletPointEditor: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ value, onChange }) => {
  const bullets = value.length ? value : [''];

  const updateBullet = (index: number, nextValue: string) => {
    onChange(bullets.map((bullet, bulletIndex) => (
      bulletIndex === index ? nextValue : bullet
    )));
  };

  const deleteBullet = (index: number) => {
    onChange(bullets.filter((_, bulletIndex) => bulletIndex !== index));
  };

  const addBullet = () => {
    onChange([...bullets, '']);
  };

  return (
    <div className="custom-product-bullet-editor-list">
      {bullets.map((bullet, index) => (
        <div key={index} className="custom-product-bullet-editor-row">
          <span className="custom-product-bullet-number">{index + 1}</span>
          <div>
            <Input.TextArea
              value={bullet}
              style={{ width: '100%' }}
              autoSize={{ minRows: 2 }}
              onChange={(event) => updateBullet(index, event.target.value)}
              autoFocus={index === 0}
            />
            <div className="custom-product-length-tags">
              <LengthTags value={bullet} />
            </div>
          </div>
          <Tooltip title="Delete bullet">
            <Button
              aria-label={`Delete bullet ${index + 1}`}
              icon={<DeleteOutlined />}
              danger
              type="text"
              onClick={() => deleteBullet(index)}
            />
          </Tooltip>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={addBullet}>
        Add bullet
      </Button>
    </div>
  );
};

const GeneratedContentSection: React.FC<Props> = ({ product }) => {
  const dispatch = useAppDispatch();
  const selectedCompany = useAppSelector((state) => state.companies.selectedCompany);

  const [activeEditField, setActiveEditField] = useState<EditableGeneratedField | null>(null);
  const [savingGen, setSavingGen] = useState<boolean>(false);
  const [genFormValues, setGenFormValues] = useState(product.amazonGeneration);

  useEffect(() => {
    setGenFormValues(product.amazonGeneration);
  }, [product.amazonGeneration]);

  if (!product.amazonGeneration) {
    return (
      <div className="custom-product-detail-scroll">
        <div className="custom-product-empty">No content available yet.</div>
      </div>
    );
  }

  const globalKeywords = deriveGenerationAnchorKeywords(
    product.input.globalMustHaveKeywords || [],
    product.input.brand,
  );
  const titleKeywords = getCoverageKeywords(
    globalKeywords,
    product.input.titleHighPriorityKeywords || [],
    product.input.titleOptionalKeywords || [],
  );
  const titlePriorityKeywords = getPriorityCoverageKeywords(
    globalKeywords,
    product.input.titleHighPriorityKeywords || [],
  );
  const bulletPointKeywords = getCoverageKeywords(
    globalKeywords,
    product.input.bulletPointHighPriorityKeywords || [],
    product.input.bulletPointOptionalKeywords || [],
  );
  const bulletPointPriorityKeywords = getPriorityCoverageKeywords(
    globalKeywords,
    product.input.bulletPointHighPriorityKeywords || [],
  );
  const descriptionKeywords = getCoverageKeywords(
    globalKeywords,
    product.input.descriptionHighPriorityKeywords || [],
    product.input.descriptionOptionalKeywords || [],
  );
  const descriptionPriorityKeywords = getPriorityCoverageKeywords(
    globalKeywords,
    product.input.descriptionHighPriorityKeywords || [],
  );
  const oldProductContents = product.input.productContents || '';
  const oldKeyBenefits = getValuesFromRecord(product.input.keyBenefit);
  const generatedDescriptionText = getTextFromHtml(product.amazonGeneration.description);
  const seoImpact = calculateSeoImpact({
    fields: [
      {
        label: 'Title',
        oldText: product.input.productName || '',
        newText: product.amazonGeneration.productTitle || '',
        keywords: titleKeywords,
        priorityKeywords: titlePriorityKeywords,
        baseWeight: 3,
        priorityWeight: 5,
      },
      {
        label: 'Bullet points',
        oldText: oldKeyBenefits.join('\n'),
        newText: (product.amazonGeneration.bulletPoints || []).join('\n'),
        keywords: bulletPointKeywords,
        priorityKeywords: bulletPointPriorityKeywords,
        baseWeight: 2,
        priorityWeight: 4,
      },
      {
        label: 'Description',
        oldText: oldProductContents,
        newText: generatedDescriptionText,
        keywords: descriptionKeywords,
        priorityKeywords: descriptionPriorityKeywords,
        baseWeight: 1,
        priorityWeight: 2,
      },
    ],
    negativeKeywords: product.input.negativeKeywords || [],
    generatedTitle: product.amazonGeneration.productTitle || '',
    generatedBulletPoints: product.amazonGeneration.bulletPoints || [],
    generatedDescription: generatedDescriptionText,
    amazonContentSettings: selectedCompany?.amazonContentSettings,
  });

  const discardInlineEdit = () => {
    setGenFormValues(product.amazonGeneration);
    setActiveEditField(null);
  };

  const saveInlineEdit = async () => {
    if (!selectedCompany || !genFormValues || !product.amazonGeneration) return;

    try {
      setSavingGen(true);
      const updatedGen: CustomProductGeneration = {
        ...product.amazonGeneration,
        productTitle: genFormValues.productTitle,
        bulletPoints: genFormValues.bulletPoints,
        description: genFormValues.description,
      };
      await dispatch(updateCustomProduct({
        id: product.id,
        companyId: selectedCompany.id,
        amazonGeneration: updatedGen,
      })).unwrap();
      await dispatch(refetchCustomProduct({
        companyId: selectedCompany.id,
        customProductId: product.id,
      }));
      notification.success({ message: 'Generated content updated' });
      setActiveEditField(null);
    } catch (e) {
      notification.error({ message: 'Failed to update generated content' });
    } finally {
      setSavingGen(false);
    }
  };

  return (
    <div className="custom-product-detail-scroll">
      <div className="custom-product-section">
        <SeoImpactPanel impact={seoImpact} />

        {activeEditField === 'title' ? (
          <InlineEditBlock
            label="Generated Title"
            oldLabel="Old Product Title"
            newLabel="Generated Product Title"
            oldContent={product.input.productName}
            saving={savingGen}
            onSave={saveInlineEdit}
            onDiscard={discardInlineEdit}
          >
            <Input
              value={genFormValues?.productTitle}
              onChange={(e) => setGenFormValues((prev: CustomProductGeneration | undefined) => prev ? ({ ...prev, productTitle: e.target.value }) : prev)}
              autoFocus
            />
          </InlineEditBlock>
        ) : (
          <ComparisonBlock
            label="Generated Title"
            oldLabel="Old Product Title"
            newLabel="Generated Product Title"
            oldText={product.input.productName}
            newText={product.amazonGeneration.productTitle}
            keywords={titleKeywords}
            lengthTags={<LengthTags value={product.amazonGeneration.productTitle} />}
            onEdit={() => setActiveEditField('title')}
          />
        )}

        {activeEditField === 'bulletPoints' ? (
          <InlineEditBlock
            label="Generated Bullet Points"
            oldLabel="Old Key Benefits"
            newLabel="Generated Bullet Points"
            oldContent={oldKeyBenefits.join('\n')}
            saving={savingGen}
            onSave={saveInlineEdit}
            onDiscard={discardInlineEdit}
          >
            <BulletPointEditor
              value={genFormValues?.bulletPoints || []}
              onChange={(vals) => setGenFormValues((prev: CustomProductGeneration | undefined) => prev ? ({ ...prev, bulletPoints: vals }) : prev)}
            />
          </InlineEditBlock>
        ) : (
          <BulletPointComparisonBlock
            oldBulletPoints={oldKeyBenefits}
            newBulletPoints={product.amazonGeneration.bulletPoints}
            keywords={bulletPointKeywords}
            onEdit={() => setActiveEditField('bulletPoints')}
          />
        )}

        {activeEditField === 'description' ? (
          <InlineEditBlock
            label="Generated Description"
            oldLabel="Old Product Contents"
            newLabel="Generated Description"
            oldContent={oldProductContents}
            saving={savingGen}
            onSave={saveInlineEdit}
            onDiscard={discardInlineEdit}
          >
            <Input.TextArea
              value={genFormValues?.description}
              autoSize={{ minRows: 6 }}
              onChange={(e) => setGenFormValues((prev: CustomProductGeneration | undefined) => prev ? ({ ...prev, description: e.target.value }) : prev)}
              autoFocus
            />
          </InlineEditBlock>
        ) : (
          <ComparisonBlock
            label="Generated Description"
            oldLabel="Old Product Contents"
            newLabel="Generated Description"
            oldText={oldProductContents}
            newText={generatedDescriptionText}
            keywords={descriptionKeywords}
            lengthTags={<LengthTags value={generatedDescriptionText} showWordCount />}
            onEdit={() => setActiveEditField('description')}
          />
        )}
      </div>
    </div>
  );
};

export { GeneratedContentSection };
